import { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from './icons';
import { CURRENCIES, RATES_DATE, useCurrency } from '../lib/currency';
import { useT } from '../i18n';

/**
 * The currency switch in the header — five currencies, USD first. Replaces
 * the region menu: country and language were decoration (Germany and the
 * Netherlands both mean EUR and the page didn't change language), currency
 * is the thing a shopper abroad actually wants. Language comes back as its
 * own control once the UI is translated.
 */
const CurrencyMenu = () => {
  const { currency, setCurrency } = useCurrency();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = CURRENCIES.find((c) => c.code === currency) ?? CURRENCIES[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="relative inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-[13.5px] font-medium tracking-[0.01em] text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground max-md:after:absolute max-md:after:-inset-y-1"
      >
        <span className="sr-only">{t('Currency: ')}</span>
        <span className="tabular">{current.symbol}</span>
        {current.code}
        <ChevronDownIcon className="size-3.5 opacity-70" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('Currency')}
          className="absolute right-0 top-full z-30 mt-1.5 w-[264px] overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-lg"
        >
          {CURRENCIES.map((c) => {
            const active = c.code === currency;
            return (
              <button
                key={c.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  setCurrency(c.code);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-3 px-3 py-3 text-left md:py-2 text-[13.5px] hover:bg-muted"
              >
                <span className="tabular w-10 shrink-0 text-muted-foreground">{c.symbol}</span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{c.code}</span>
                  <span className="ml-2 text-muted-foreground">{t(c.name)}</span>
                </span>
                {active && <CheckIcon className="size-4 shrink-0 text-primary" />}
              </button>
            );
          })}
          <p className="mt-1 border-t border-border px-3 pb-1.5 pt-2 text-[11.5px] leading-relaxed text-muted-foreground">
            {t('Prices are listed in USD; each marketplace shows its own currency at checkout. Other currencies are approximate, at rates')}
            {RATES_DATE ? t(' updated {date}', { date: RATES_DATE }) : t(' updated daily')}.
          </p>
        </div>
      )}
    </div>
  );
};

export default CurrencyMenu;
